// POST /api/customerLookup
import { useMutation } from '@tanstack/react-query';
import { CustomerAccountDetailsSchema } from 'packages/cosell/src/pages/microsoft/ReferralForm/CustomerDetailsFormSection/customerAccountTransformSchema';
import { z } from 'zod';
import { coSellMsftApi } from '../tackleApiClient';

export const CustomerLookupInputAddressSchema = z
  .object({
    addressLine1: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
    postalCode: z.string().nullable().optional(),
  })
  .nullable()
  .optional();

export const CustomerLookupInputSchema = z
  .object({
    companyName: z.string(),
    address: CustomerLookupInputAddressSchema,
  })
  .partial()
  .nullable()
  .optional();

export type CustomerLookupInput = z.infer<typeof CustomerLookupInputSchema>;

export const customerLookup__CustomerAccountSchema = z.object({
  childAccounts: z
    .array(
      z
        .object({
          customerAccountDetails: CustomerAccountDetailsSchema.partial()
            .nullable()
            .optional(),
          childAccounts: z
            .array(
              z
                .object({
                  customerAccountDetails: CustomerAccountDetailsSchema.partial()
                    .nullable()
                    .optional(),
                  childAccounts: z.array(z.unknown().nullable().optional()),
                })
                .partial()
                .nullable()
                .optional(),
            )
            .nullable()
            .optional(),
        })
        .partial()
        .nullable()
        .optional(),
    )
    .nullable()
    .optional(),
  customerAccountDetails: CustomerAccountDetailsSchema.partial()
    .nullable()
    .optional(),
});

export const customerLookup__CustomerAccountsSchema = z
  .array(customerLookup__CustomerAccountSchema.partial().nullable().optional())
  .nullable()
  .optional();

export const customerLookup__Response = z
  .object({
    managedAccounts: customerLookup__CustomerAccountsSchema,
    unManagedAccounts: customerLookup__CustomerAccountsSchema,
  })
  .partial();

export type CustomerLookupResponseAccounts = z.infer<
  typeof customerLookup__CustomerAccountsSchema
>;

export type CustomerLookupResponseAccount = z.infer<
  typeof customerLookup__CustomerAccountSchema
>;

export type CustomerLookupResponse = z.infer<typeof customerLookup__Response>;

export type CustomerLookupMutationVariables = {
  body: CustomerLookupInput;
};

export async function customerLookupRequest({
  body,
}: CustomerLookupMutationVariables) {
  try {
    const parsedBody = CustomerLookupInputSchema.parse(body);

    const response = await coSellMsftApi
      .post('api/customerLookup', {
        headers: {},
        body: JSON.stringify(parsedBody),
      })
      .json();

    const parsedResponse = customerLookup__Response.parse(response);

    return parsedResponse;
  } catch (error) {
    reportError(error);
    throw error;
  }
}

export function useCustomerLookup() {
  return useMutation({
    mutationFn: customerLookupRequest,
    mutationKey: ['customer-lookup'],
  });
}
